import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import {
  ALPHA_START_ALNUM_REGEX,
  AT_LEAST_ONE_ALPHABET,
  VALID_EMAIL_PATTERN_REGEX,
} from '../../../shared/utils/constants';
import { ApiService } from '../../../shared/services/api.service';
import {
  CreateOrganizationMutation,
  CreateOrganizationMutationVariables,
  CreateUserMutation,
  CreateUserMutationVariables,
  DeleteOrganizationBucketMutation,
  DeleteOrganizationBucketMutationVariables,
  GetRolesQuery,
  GetRolesQueryVariables,
  Organization,
  UpdateOrganizationDatabricksFlagMutation,
  UpdateOrganizationDatabricksFlagMutationVariables,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  UserActions,
} from '../../../../API';
import { GetRoles } from '../../../../graphql/queries';
import { AuthService } from '../../../shared/services/auth.service';
import { DisplayFlattenUser, orgTypeOptions, SelectOption } from '../../models/users';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import {
  CreateOrganization,
  CreateUser,
  DeleteOrganizationBucket,
  UpdateOrganizationDatabricksFlag,
  UpdateUser,
} from '../../../../graphql/mutations';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss',
})
export class CreateUserComponent implements OnInit, OnDestroy {
  createUserForm!: FormGroup;
  readonly userData: DisplayFlattenUser = inject(NZ_MODAL_DATA);
  isLoading: boolean = false;
  roleOptions: SelectOption[] = [];
  ENV: string = 'dev';
  bucketName?: string;
  isBucketSetLocally: boolean = false;
  orgList?: Organization[];
  orgTypeOptions?: SelectOption[] = [];
  isValidOrgType: boolean = false;

  constructor(
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    private nzModalRef: NzModalRef,
    private apiService: ApiService,
    private authService: AuthService,
    private appService: AppService,
    private userManagementService: UserManagementService,
  ) {
    this.ENV = this.appService.ENV;
    this.initializeForm();
  }

  ngOnInit(): void {
    this.orgTypeOptions = orgTypeOptions;
    this.getRoles();
    this.orgList = this.userManagementService.orgList;

    this.userWillExportViaSFTPControl?.valueChanges.subscribe((newValue) => {
      if (newValue == true && this.orgNameFormControl.invalid) {
        this.nzMessageService.error('Please enter valid organization name');
        this.userWillExportViaSFTPControl.setValue(false);
        return;
      }

      if (newValue == true && this.orgNameFormControl.valid && !this.bucketName) {
        this.bucketName = `dcr-${this.ENV}-${this.orgNameFormControl.value}-stage-zone`;
        this.isBucketSetLocally = true;
      }
    });

    this.userWillExportViaDatabricksControl?.valueChanges.subscribe((newValue) => {
      if (newValue == true && this.orgNameFormControl.invalid) {
        this.nzMessageService.error('Please enter valid organization name');
        this.userWillExportViaDatabricksControl.setValue(false);
        return;
      }
    });

    this.orgNameFormControl.valueChanges.subscribe((newValue) => {
      const isDatabricksEnabled =
        this.orgList?.find((org) => org?.orgName === newValue)?.isDatabricksConnectionAllowed || false;
      this.bucketName = this.orgList?.find((org) => org?.orgName === newValue)?.s3Bucket || '';
      if (this.bucketName && !this.isBucketSetLocally) {
        this.userWillExportViaSFTPControl.setValue(true);
      } else {
        this.userWillExportViaSFTPControl.reset();
        this.userWillExportViaSFTPControl.enable();
      }
      if (isDatabricksEnabled) {
        this.userWillExportViaDatabricksControl.setValue(true);
      } else {
        this.userWillExportViaDatabricksControl.reset();
        this.userWillExportViaDatabricksControl.enable();
      }

      const orgType = this.userManagementService.orgList?.find((org) => org?.orgName === newValue)?.orgType || '';
      this.orgTypeFormControl.setValue(orgType);
    });

    this.orgTypeFormControl.valueChanges.subscribe((newValue) => {
      this.isValidOrgType = true;
      if (!newValue) {
        this.orgTypeFormControl.reset();
        this.isValidOrgType = false;
      }
    });

    if (this.userData) {
      const { name, organization, orgType, role, email } = this.userData;
      const [firstName, lastName] = name?.trim().split(' ') || '';
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const roleValue = Object.entries(this.authService.groupRoleMap).find(([_, v]) => v === role)?.[0];

      this.createUserForm.patchValue({
        firstName: firstName,
        lastName: lastName,
        email: email,
        orgName: organization,
        orgType: orgType,
        role: roleValue,
      });

      this.emailFormControl.disable();
      this.orgTypeFormControl.disable();
    }
  }

  ngOnDestroy(): void {
    this.isLoading = false;
    this.createUserForm.reset();
  }

  private initializeForm(): void {
    this.createUserForm = this.fb.nonNullable.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
        ],
      ],
      email: ['', [Validators.required, Validators.pattern(VALID_EMAIL_PATTERN_REGEX), Validators.maxLength(100)]],
      orgName: [''],
      orgType: [''],
      userWillExportViaSFTP: [false],
      userWillExportViaDatabricks: [false],
      role: ['', Validators.required],
    });

    this.updateConditionalValidators();
  }

  private updateConditionalValidators(): void {
    if (this.isNFLAdmin) {
      // Set validators for NFL admin users
      this.orgNameFormControl?.setValidators([
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(40),
        Validators.pattern(ALPHA_START_ALNUM_REGEX),
      ]);

      this.orgTypeFormControl.setValidators([Validators.required]);
    } else {
      this.orgNameFormControl?.clearValidators();
      this.orgTypeFormControl?.clearValidators();

      this.orgNameFormControl?.setValue(this.userManagementService.getOrgType());
      this.orgTypeFormControl?.setValue(this.userManagementService.getOrganizationType());

      this.isValidOrgType = true; // This is will be true for non NFL admin, it will always have orgType value from loggedIn user.

      this.userWillExportViaSFTPControl?.setValue(false);
    }
    this.orgNameFormControl?.updateValueAndValidity();
  }

  getRoles(): void {
    this.isLoading = true;

    this.apiService
      .query<GetRolesQuery, GetRolesQueryVariables>(GetRoles)
      .then((response) => {
        const roles = response.data?.GetRoles;

        this.roleOptions = (roles || []).map((roleData) => ({
          name: this.authService.groupRoleMap[roleData.role || ''],
          value: roleData.role || '',
        }));
      })
      .catch((err) => {
        console.error('Error fetching roles:', err);
        this.nzMessageService.error('Error fetching roles. Please try again.');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  handleCreateUser(): void {
    this.isLoading = true;
    const functionArr = [this.createUser(), this.createOrganization()];

    if (this.userWillExportViaSFTPControl.value === false && this.bucketName) {
      functionArr.push(this.deleteBucket());
    }
    Promise.all(functionArr)
      .then(async ([userResponse, OrganizationResponse]) => {
        const userResult = userResponse?.data.CreateUser;
        const orgResult =
          OrganizationResponse?.data.CreateOrganization || OrganizationResponse?.data.DeleteOrganizationBucket;

        await this.handleCreateAndUpdateUserResponse(userResult, orgResult, 'create');
      })
      .catch((err) => {
        console.error('err', err);
        this.nzMessageService.error('Failed to create user.');
      })
      .finally(() => (this.isLoading = false));
  }

  handleUpdateUser(): void {
    this.isLoading = true;
    const functionArr = [this.updateUser()];

    if (this.userWillExportViaSFTPControl.value === true) {
      // this call will update bucket name in organization table
      functionArr.push(this.createOrganization());
    } else if (this.userWillExportViaSFTPControl.value === false && this.bucketName) {
      functionArr.push(this.deleteBucket());
    }

    if (
      this.userWillExportViaDatabricksControl.value != null &&
      this.userWillExportViaDatabricksControl.value !== undefined
    ) {
      functionArr.push(this.updateDatabricksFlag());
    }

    Promise.all(functionArr)
      .then(async (responses) => {
        const userResult = responses[0]?.data?.UpdateUser;
        const orgResponse = responses.find((r) => r?.data?.CreateOrganization || r?.data?.DeleteOrganizationBucket);
        const orgResult = orgResponse?.data?.CreateOrganization || orgResponse?.data?.DeleteOrganizationBucket;

        const updateDatabricksFlagResponse = responses.find((r) => r?.data?.UpdateOrganizationDatabricksFlag);
        const updateDatabricksFlagResult = updateDatabricksFlagResponse?.data?.UpdateOrganizationDatabricksFlag;

        if (updateDatabricksFlagResult?.success !== true) {
          this.nzMessageService.error('User updated but failed to update Databricks settings.');
        }
        await this.handleCreateAndUpdateUserResponse(userResult, orgResult || updateDatabricksFlagResult, 'update');
      })
      .catch((err) => {
        console.error('err', err);
        this.nzMessageService.error('Failed to update user.');
      })
      .finally(() => (this.isLoading = false));
  }

  handleCancel(): void {
    this.nzModalRef.close(false);
  }

  createUser(): Promise<any> {
    if (!this.isNFLAdmin) {
      this.orgNameFormControl.setValue(this.userManagementService.getOrgType());
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { userWillExportViaSFTP, userWillExportViaDatabricks, orgName, orgType, ...restFields } =
      this.createUserForm.value;
    return this.apiService.query<CreateUserMutation, CreateUserMutationVariables>(CreateUser, {
      userRequest: { ...restFields, orgType: orgName },
    });
  }

  createOrganization(): Promise<any> {
    const bucketName = this.bucketName ? this.bucketName : undefined;
    const { userWillExportViaDatabricks } = this.createUserForm.value;
    return this.apiService.query<CreateOrganizationMutation, CreateOrganizationMutationVariables>(CreateOrganization, {
      organizationInput: {
        orgType: this.orgTypeFormControl.value,
        orgName: this.orgNameFormControl.value,
        s3Bucket: bucketName,
        isDatabricksConnectionAllowed: userWillExportViaDatabricks,
      },
    });
  }

  deleteBucket(): Promise<any> {
    return this.apiService.query<DeleteOrganizationBucketMutation, DeleteOrganizationBucketMutationVariables>(
      DeleteOrganizationBucket,
      {
        deleteBucketInput: { orgName: this.orgNameFormControl.value },
      },
    );
  }

  async handleCreateAndUpdateUserResponse(userResult: any, orgResult: any, action: 'create' | 'update') {
    if (orgResult) {
      await this.userManagementService.getOrgList();
      this.orgList = this.userManagementService.orgList;
    }

    if (userResult.success === true && !orgResult) {
      this.nzMessageService.success(`User ${action}d successfully`);
      this.nzModalRef.triggerOk();
      return;
    }

    if (userResult.success !== true) {
      this.nzMessageService.error(userResult.message || `Failed to ${action} user`);
      this.nzModalRef.triggerOk();
      return;
    }

    if (userResult.success === true && orgResult.success === false) {
      let errorMessage = `User is created but bucket assignment failed due to bucket ${this.bucketName} does not exist or is inaccessible. `;

      if (action === 'update') {
        errorMessage = `User is partially updated but bucket assignment failed due to bucket ${this.bucketName} does not exist or is inaccessible.`;
      }
      this.nzMessageService.error(errorMessage);
      this.nzModalRef.triggerOk();
      return;
    }

    if (userResult.success === true && orgResult.success === true) {
      this.nzMessageService.success(`User ${action}d successfully`);
      this.nzModalRef.triggerOk();
    }
  }

  updateUser(): Promise<any> {
    const { userWillExportViaSFTP, role, orgName, firstName, lastName } = this.createUserForm.value;

    return this.apiService.query<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUser, {
      userRequest: {
        action: UserActions.UPDATE_USER,
        cognitoUserSub: this.userData.sub!,
        orgType: orgName,
        role,
        firstName,
        lastName,
        isNFLAdmin: this.isNFLAdmin,
        SFTPEnabled: userWillExportViaSFTP,
      },
    });
  }

  get userWillExportViaSFTPControl(): UntypedFormControl {
    return this.createUserForm.get('userWillExportViaSFTP') as UntypedFormControl;
  }

  get isNFLAdmin(): boolean {
    return this.userManagementService.getOrgType() == 'nfl';
  }

  get orgTypeFormControl(): UntypedFormControl {
    return this.createUserForm.get('orgType') as UntypedFormControl;
  }

  get orgNameFormControl(): UntypedFormControl {
    return this.createUserForm.get('orgName') as UntypedFormControl;
  }

  get emailFormControl(): UntypedFormControl {
    return this.createUserForm.get('email') as UntypedFormControl;
  }

  get userWillExportViaDatabricksControl(): UntypedFormControl {
    return this.createUserForm.get('userWillExportViaDatabricks') as UntypedFormControl;
  }

  updateDatabricksFlag(): Promise<any> {
    const { userWillExportViaDatabricks } = this.createUserForm.value;
    return this.apiService.query<
      UpdateOrganizationDatabricksFlagMutation,
      UpdateOrganizationDatabricksFlagMutationVariables
    >(UpdateOrganizationDatabricksFlag, {
      updateDatabricksFlagInput: {
        orgName: this.orgNameFormControl.value,
        isDatabricksConnectionAllowed: userWillExportViaDatabricks,
      },
    });
  }
}
